import React, { useState } from "react";
import { useAuth } from "../../util/auth";
import { useUser, updateUserDnd } from "../../util/db";

export default function DndUserSettings() {
  const auth = useAuth();
  const {
    data: user,
    error: userError,
    status: userStatus,
  } = useUser(auth.user.uid);

  const [updating, setUpdating] = useState(null);

  async function handleToggle(field) {
    setUpdating(field);
    try {
      const changes = {
        dndEmail: field === "dndEmail" ? !user.dndEmail : (user.dndEmail || false),
        dndSms: field === "dndSms" ? !user.dndSms : (user.dndSms || false),
        dndAll: user.dndAll || false,
      };
      await updateUserDnd(auth.user.uid, changes, "app_user");
    } catch (err) {
      alert("Failed to update preference: " + err.message);
    } finally {
      setUpdating(null);
    }
  }

  if (userStatus === "loading") {
    return <p className="text-sm text-gray-500 py-4">Loading your preferences...</p>;
  }

  if (userStatus === "error") {
    return (
      <p className="text-sm text-red-500 py-4">
        Failed to load your preferences. Please refresh the page.
      </p>
    );
  }

  if (!user) {
    return <p className="text-sm text-gray-500 py-4">No user data found.</p>;
  }

  const dndEmail = user.dndEmail || false;
  const dndSms = user.dndSms || false;

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        Control which types of communications you receive. When you opt out of a
        channel, you will no longer receive messages through that channel. You
        can change these settings at any time.
      </p>

      {/* Email Toggle */}
      <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">Email Communications</p>
          <p className="text-sm text-gray-500 mt-0.5">
            {dndEmail
              ? "You have opted out of email communications."
              : "You are currently receiving email communications."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => handleToggle("dndEmail")}
          disabled={updating === "dndEmail"}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
            dndEmail
              ? "bg-red-500 focus:ring-red-500"
              : "bg-green-500 focus:ring-green-500"
          }`}
          role="switch"
          aria-checked={dndEmail}
          aria-label="Toggle email DND"
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              dndEmail ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>

      {/* SMS Toggle */}
      <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">SMS Communications</p>
          <p className="text-sm text-gray-500 mt-0.5">
            {dndSms
              ? "You have opted out of SMS communications."
              : "You are currently receiving SMS communications."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => handleToggle("dndSms")}
          disabled={updating === "dndSms"}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
            dndSms
              ? "bg-red-500 focus:ring-red-500"
              : "bg-green-500 focus:ring-green-500"
          }`}
          role="switch"
          aria-checked={dndSms}
          aria-label="Toggle SMS DND"
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              dndSms ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>

      {(dndEmail || dndSms) && (
        <div className="rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-3">
          <p className="text-sm text-yellow-700">
            You have opted out of {dndEmail && dndSms ? "email and SMS" : dndEmail ? "email" : "SMS"} communications.
            You may miss important updates and notifications while opted out.
          </p>
        </div>
      )}
    </div>
  );
}
